import { useCallback, useEffect, useMemo, useState } from 'react';
import { usersApi } from '../../api/users';
import { useDebounce } from '../../hooks/useDebounce';

const COUNT_OF_ITEMS_PER_PAGE = 20;

export const useUsers = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rawUsers, setRawUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({ totalNodes: 0 });

  const debouncedSearch = useDebounce(searchQuery, 250);

  const fetchUsers = useCallback(async (search: string, p: number) => {
    setLoading(true);
    try {
      const res = await usersApi.list({
        search: search || undefined,
        page: p,
        per_page: COUNT_OF_ITEMS_PER_PAGE,
      });
      const data = res.data;
      setRawUsers((prev) => (p === 1 ? data.nodes : [...prev, ...data.nodes]));
      const hasMore = data.page * data.per_page < data.total;
      setPagination({
        totalNodes: data.total,
        currentPage: data.page,
        perPage: data.per_page,
        nextPage: hasMore ? data.page + 1 : null,
      });
    } catch {
      setRawUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchUsers(debouncedSearch, 1);
  }, [debouncedSearch, fetchUsers]);

  const usersList = useMemo(() => {
    return (
      loading && rawUsers.length === 0 ? Array(COUNT_OF_ITEMS_PER_PAGE).fill({}) : rawUsers
    ).map((item: any) => ({
      role: item.roles?.[0] ?? item.role ?? null,
      email: item.email,
      refId: item.ref_id ?? item.refId,
      active: item.active,
      fullName: item.full_name ?? item.fullName,
      speciality: item.speciality,
      forceResetPassword: item.force_reset_password ?? item.forceResetPassword,
      id: item.id,
    }));
  }, [rawUsers, loading]);

  const onScroll = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUsers(searchQuery, nextPage);
  }, [page, searchQuery, fetchUsers]);

  const onSearch = useCallback((q: string) => setSearchQuery(q), []);

  const refetchItems = useCallback(() => {
    setPage(1);
    return fetchUsers(searchQuery, 1);
  }, [searchQuery, fetchUsers]);

  return {
    loading,
    usersList,
    searchQuery,
    pagination,
    onScroll,
    onSearch,
    refetchItems,
  };
};
